import { createApi } from '@reduxjs/toolkit/query/react';
import { SourcingRequest, SourcingRequestMatch } from '@scaleup/models';
import { baseQuery } from './baseQuery';
import { TagTypes } from './tagTypes';

export const sourcingApi = createApi({
  reducerPath: 'sourcingApi',
  baseQuery: baseQuery,
  tagTypes: [TagTypes.sourcingRequests, TagTypes.sourcingRequestsMatches],
  endpoints: (builder) => ({
    getSourcingRequests: builder.query<SourcingRequest[], void>({
      query: () => `/sourcing_request`,
      providesTags: [TagTypes.sourcingRequests],
    }),

    enhanceSourcingPrompt: builder.mutation<{ prompt: string }, string>({
      query: (entry) => ({
        url: '/sourcing_request/enhance',
        method: 'POST',
        body: { entry },
      }),
    }),

    createSourcingRequest: builder.mutation<SourcingRequest, string>({
      query: (entry) => ({
        url: '/sourcing_request',
        method: 'POST',
        body: { entry },
      }),
      invalidatesTags: [TagTypes.sourcingRequests, TagTypes.user],
    }),
    submitFeedback: builder.mutation<
      void,
      {
        requestId: string;
        rating: number;
        comment?: string;
      }
    >({
      query: ({ requestId, ...rest }) => ({
        url: `/sourcing_request/${requestId}/feedback`,
        method: 'POST',
        body: { ...rest },
      }),
      invalidatesTags: [TagTypes.sourcingRequests],
    }),
    createSourcingRequestOverride: builder.mutation<SourcingRequest, any>({
      query: (body) => ({
        url: '/sourcing_request/override',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TagTypes.sourcingRequests, TagTypes.user],
    }),
    getSourcingRequestMatches: builder.query<SourcingRequestMatch[], string>({
      query: (requestId) => `/sourcing_request/${requestId}/match`,
      providesTags: (_result, _error, id) => [
        { type: TagTypes.sourcingRequestsMatches, id },
      ],
    }),
    setMatchesViewed: builder.mutation<void, string>({
      query: (requestId) => ({
        url: `/sourcing_request/${requestId}/viewed_matches`,
        method: 'PUT',
        body: { viewedMatches: true },
      }),
    }),
    getCheckoutUrl: builder.query<any, { recurrence: 'monthly' | 'yearly' }>({
      query: ({ recurrence }) =>
        `/subscription/checkout?subscription=professional&recurrence=${recurrence}`,
    }),
  }),
});

export const {
  useGetSourcingRequestsQuery,
  useCreateSourcingRequestOverrideMutation,
  useCreateSourcingRequestMutation,
  useGetSourcingRequestMatchesQuery,
  useSetMatchesViewedMutation,
  useLazyGetCheckoutUrlQuery,
  useEnhanceSourcingPromptMutation,
  useSubmitFeedbackMutation,
} = sourcingApi;
